import React from 'react';
import styled from '@emotion/styled';
import { BulkSearchState } from "@/redux/slices/slice_types";
import { PolarisIcon } from '@/exports/columnCommonComponents';
import { alpha } from '@mui/material';

type ErrorListProps = {
  list: BulkSearchState['errors'];
  onClearAll?: () => void;
  onExport?: () => void;
  onRemoveError?: (id: string) => void;
};

const Container = styled.div`
  border-radius: 8px;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
  padding: 12px 16px;
`;

const HeaderButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.palette.default.main};
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    color: ${props => alpha(props.theme.palette.default.main, 0.6)};
  }
`;

const ErrorListContainer = styled.div`
  max-height: 500px;
  overflow-y: auto;
`;

const ErrorItem = styled.div`
  display: flex;
  align-items: center;
`;

const ErrorItemContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  padding: ${props => props.theme.spacing(1, 2)};
  margin-bottom: ${props => props.theme.spacing(1)};
  border-radius: 32px;
  border: 1px solid ${props => props.theme.palette.error.main};
  background: ${props => alpha(props.theme.palette.error.main, 0.1)};
  
  &:last-child {
    border-bottom: none;
  }
`;

const ErrorIcon = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
`;

const ErrorText = styled.div`
  display: flex;
  color: ${props => props.theme.palette.error.main};
  font-size: 14px;
  flex: 1;
`;

const ErrorId = styled.div`
  display: inline-flex;
  align-items: center;
  min-width: 80px;
  margin-right: ${props => props.theme.spacing(2)};
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.7;
  }
`;

const ErrorList = ({ list, onClearAll, onRemoveError }: ErrorListProps) => {
  if (!list || list.length === 0) {
    return null;
  }

  const handleExportCSV = () => {
    if (!list || list.length === 0) return;
    
    // Create CSV content with IDs
    const csvContent = list.map(error => [error.id, error.error].join(',')).join('\n');
    
    // Create blob with CSV content
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Create download link
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `error-list-${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up URL object
    URL.revokeObjectURL(url);
  };

  return (
    <Container>
      <Header>
        <HeaderButton onClick={onClearAll}>
          Clear all <PolarisIcon icon="close" height={16} />
        </HeaderButton>
        <HeaderButton onClick={handleExportCSV}>
          Export <PolarisIcon icon="download" height={16} />
        </HeaderButton>
      </Header>
      <ErrorListContainer>
        {list.map((error, index) => (
          <ErrorItem key={`${error.id}-${index}`}>
            <ErrorItemContent>
              <ErrorIcon>
                <PolarisIcon style={{ color: window.theme.palette.error.main }} icon="info" height={16} />
              </ErrorIcon>
              <ErrorText><ErrorId>{error.id}</ErrorId>{error.error}</ErrorText>
            </ErrorItemContent>
            <RemoveButton onClick={() => onRemoveError?.(error.id)}>
              <PolarisIcon style={{ color: window.theme.palette.error.main }} icon="close" height={16} />
            </RemoveButton>
          </ErrorItem>
        ))}
      </ErrorListContainer>
    </Container>
  );
};

export default ErrorList;